import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Dropdown,
  Option,
  useToastController,
} from "@fluentui/react-components";

import DialogComponent from "../../components/dialog/DialogComponent";
import Pagination from "../../components/pagination/Pagination";
import TableComponent from "../../components/table/TableComponent";
import { AdminService, CouponDto } from "../../KulcsUzletSvc";
import { useStylesMainPage } from "../../styles/MainPageStyle";
import { notify } from "../../utils/DispatchToaster";
import { TABLE, TOAST } from "../../utils/Enums";
import { ToasterContext } from "../../utils/GlobalContexts";

export type Account = {
  createdDateTime: string;
  adminUserEmail: string;
  lastUsedDateTime: string;
  accountType: any;
  databaseGuid: string;
  accountModulStates: any[];
  baseModulLastAccountModulState?: any;
  subscriptionId: string;
  subscription?: any;
};

export type Modul = {
  name: string;
  description: string;
  clientType: any;
  isBaseModul: boolean;
  internalCode: string;
  monthPrice: number;
  fullMonthProductCode: string;
  partMonthProductCode: string;
};

export enum CouponType {
  All = "All",
  OnlyBase = "OnlyBase",
  OnlyModul = "OnlyModul",
  SpecifyModul = "SpecifyModul",
  OnlyBaseOnlyOneUser = "OnlyBaseOnlyOneUser",
}
export type ApplicationUser = {
  userName: string;
  fullName: string;
  emailAddress: string;
  isActive: boolean;
};

export default function MerchantCouponsPage() {
  const styles = useStylesMainPage();
  const [data, setData] = useState<null | CouponDto[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [merchantName, setMerchanName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasterId } = useContext(ToasterContext);
  const { dispatchToast } = useToastController(toasterId);
  const [couponToDelete, setCouponToDelete] = useState<CouponDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigateBack = () => {
    navigate("/admin/merchants");
  };

  const loadData = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const userRes = await AdminService.getAdminApplicationUser(id);
      setMerchanName(userRes.fullName ?? "");
    } catch (e) {
      console.log(e);
    }

    try {
      const couponsRes = await AdminService.getAdminApplicationusercoupons(id);
      if (couponsRes) {
        const coupons = couponsRes
          .map((r) => r.coupon)
          .filter((c) => c) as any[];

        // For each coupon, fetch usage count
        const couponsWithUsedCount = await Promise.all(
          coupons.map(async (coupon) => {
            try {
              const usagesRes = await AdminService.getAdminUsedcoupons(
                coupon.couponCode,
              );
              const usageCount =
                (usagesRes && (usagesRes as any).items?.length) || 0;
              return {
                ...coupon,
                usedCount: usageCount,
              };
            } catch (e) {
              console.warn(
                `Failed to load usages for coupon ${coupon.couponCode}`,
                e,
              );
              return {
                ...coupon,
                usedCount: 0,
              };
            }
          }),
        );

        setData(couponsWithUsedCount);
        setCurrentPage(1);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRowAction = (action: string | null | undefined, item?: any) => {
    if (action === "deleteCoupon" && item) setCouponToDelete(item);
  };

  const handleDeleteCoupon = async () => {
    if (!couponToDelete?.id) return;
    setIsDeleting(true);
    try {
      const ok = await AdminService.deleteAdminCoupon(couponToDelete.id);
      if (ok) {
        notify(TOAST.SUCCESS, "Kupon törölve!", dispatchToast);
        await loadData();
      } else {
        notify(TOAST.WARN, "A kupon törlése nem sikerült.", dispatchToast);
      }
    } catch (e) {
      console.error(e);
      notify(
        TOAST.ERROR,
        "Hiba történt a kupon törlése közben.",
        dispatchToast,
      );
    } finally {
      setIsDeleting(false);
      setCouponToDelete(null);
    }
  };

  return (
    <div className={styles.main}>
      <div
        style={{
          textAlign: "center",
          width: "100%",
          fontWeight: "bold",
          fontSize: "20px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {`${merchantName} kuponjai`}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          marginBottom: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Button onClick={navigateBack}>Vissza</Button>
          <Dropdown
            defaultValue={"10"}
            style={{ minWidth: "80px" }}
            onOptionSelect={(_, dropdownData) =>
              setPageSize(dropdownData.optionValue as string)
            }
          >
            <Option value="5">5</Option>
            <Option value="10">10</Option>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
          </Dropdown>
          <span style={{ fontSize: "14px" }}>találat oldalaként</span>
        </div>

        <Button
          appearance="primary"
          onClick={() => navigate(`/admin/trader/${id}/coupon/create`)}
        >
          Új kupon
        </Button>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={data?.length ?? 0}
        pageSize={Number(pageSize)}
        onPageChange={(p) => setCurrentPage(p)}
      />

      <TableComponent
        type={TABLE.COUPONS}
        data={data ?? []}
        isLoading={isLoading}
        handleRowAction={handleRowAction}
        pageSize={Number(pageSize)}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />

      <DialogComponent
        open={!!couponToDelete}
        setOpen={() => setCouponToDelete(null)}
        handleSave={handleDeleteCoupon}
        handleClose={() => setCouponToDelete(null)}
        okBtnText="Törlés"
        closeBtnText="Mégse"
        title="Kupon törlése"
        isModalLoading={isDeleting}
      >
        <div style={{ marginBottom: "10px" }}>
          <span style={{ fontWeight: 700, marginRight: 5 }}>
            {couponToDelete?.couponCode ?? ""}
          </span>
          <span>kupon</span>
          <div style={{ marginTop: 10 }}>
            Biztos vagy benne, hogy törölni szeretnéd a következő kupont?
          </div>
        </div>
      </DialogComponent>
    </div>
  );
}
